import React, { useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import styles from "./Contact.module.scss";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status when user starts typing again
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Create the request payload
    const payload = {
      name: formData.name,
      email: formData.email,
      message: formData.message,
      source: "website_contact_form"
    };

    try {
      const response = await fetch('https://api.vertik.ai/api/contact-us', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        console.log('Contact form submitted successfully');
        toast.success('Message sent successfully!');
        setSubmitStatus('success');
        // Reset form after successful submission
        setFormData({
          name: "",
          email: "",
          message: ""
        });
      } else {
        console.error('Failed to submit contact form:', response.status, response.statusText);
        toast.error('Failed to send message. Please try again.');
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Network error. Please check your connection and try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className={styles.contactContainer}>

      <motion.div
        initial={{ x: -100, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.2 }}
      >
      <div className={styles.contactHeading}>Let's Talk</div>
      <div className="flex justify-center items-center w-full">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-8 md:w-10 lg:w-12 h-auto"
          viewBox="0 0 34 2"
          fill="none"
        >
          <path
            d="M1 1H33"
            stroke="url(#paint0_linear_4077_8241)"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_4077_8241"
              x1="1"
              y1="2"
              x2="33"
              y2="2"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#80E5FF" />
              <stop offset="1" stopColor="#7584D6" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="text-center  mt-4 mb-16">
        <h2 className={styles.contactHead}>We're Here to Help</h2>
        <p className={styles.contactHeadDetail}>
          Get in touch with us - we'll respond within 24 hours
        </p>
      </div>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        initial={{ x: 100, opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.2 }}
        className="contactForm flex flex-col gap-4 w-full max-w-md  p-4 sm:p-6 md:p-8"
      >
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Your Name"
          required
          className="w-full px-4 py-2 text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-custom transition-colors"
        />
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Email Address"
          required
          className="w-full px-4 py-2 text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-custom transition-colors"
        />
        <textarea
          name="message"
          value={formData.message}
          onChange={handleInputChange}
          placeholder="Enter Message"
          required
          className="w-full px-4 py-2 text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-custom transition-colors resize-y min-h-[120px]"
        />
        <button 
          type="submit"
          disabled={isSubmitting}
          className={`${styles.submitBtn} w-full text-white py-3 px-6 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>

        {/* Custom Success/Error Messages */}
        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-[12px] p-3 bg-green-100 border border-green-400 text-green-700 rounded-md"
          >
            ✓ Thank you! Your message has been sent successfully. We'll get back to you soon.
          </motion.div>
        )}

        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-[12px] p-3 bg-red-100 border border-red-400 text-red-700 rounded-md"
          >
            ✗ Sorry, there was an error sending your message. Please try again.
          </motion.div>
        )}
      </motion.form>
    </section>
  );
};

export default Contact;
